import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { ArrowUpRight, ChevronDown, ChevronRight, Info, X, Scale, Package, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type {
  BookVsFmvSummaryResponse,
  BookVsFmvClassRow,
  BookVsFmvAssetRow,
  CalculationBasis,
  FinancialsGroupDimension,
  ImpairmentWatchlist,
  ImpairmentTier,
  SuggestedAction,
} from '@siliconx/shared';
import {
  basisOf,
  describeSuggestedActionBasis,
  SUGGESTED_ACTION_FORMULA_ID,
  suggestedActionFromTrend,
} from '@siliconx/shared';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { UsdCell } from '@siliconx/shared/ui/UsdCell';
import { EmptyState } from '@siliconx/shared/ui/state';
import { MetricGrid } from '@siliconx/shared/ui/MetricGrid';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { api, throwIfNotOk } from '../../lib/api';
import { formatUsd } from '../../lib/format';
import { FinancialsReportScreen } from '../../components/financials/FinancialsReportScreen';
import { CoverageStatCard } from '../../components/financials/CoverageStatCard';
import { ImportFromErpLink } from '../../components/financials/ImportFromErpLink';
import { CalculationBasisTrigger } from '../../components/financials/CalculationBasisDrawer';
import { InfoTooltip } from '../../components/financials/InfoTooltip';
import { GroupByControl } from '../../components/financials/GroupByControl';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-249 T13 — Financials / Book vs FMV report (spec §5, §7 Wave 4). First
 * screen of the Financials section; consumes the T7 report endpoint
 * (`GET /api/client/financials/summary`, seller-only, class-aggregated by
 * default — no `assetClassId` query param here, so this is the roll-up
 * shape, not the per-asset drill-down).
 */
export const Route = createFileRoute('/financials/book-vs-fmv')({
  component: BookVsFmvPage,
  // SIL-566 (I2-B4) — cross-pillar deep link from Market Outlook's per-SKU
  // card: ?classId=<owned asset class> opens (and scrolls to) that class's
  // row in the by-asset-class table below, same target `revealClass` already
  // uses for the impairment watchlist's own in-page links.
  validateSearch: (search: Record<string, unknown>): { classId?: string } => ({
    classId: typeof search.classId === 'string' && search.classId ? search.classId : undefined,
  }),
});

const POLICY_BANNER_DISMISS_KEY = 'siliconx-financials-policy-banner-dismissed';

const DIMENSION_NOUN: Record<FinancialsGroupDimension, string> = {
  class: 'asset class',
  lot: 'lot',
  team: 'team',
  site: 'site',
};

const DIMENSION_NOUN_CAPITALIZED: Record<FinancialsGroupDimension, string> = {
  class: 'Class',
  lot: 'Lot',
  team: 'Team',
  site: 'Site',
};

const DIMENSION_SUBTITLE: Record<FinancialsGroupDimension, string> = {
  class: 'Net book value against current fair market value, by asset class.',
  lot: 'Net book value against current fair market value, by lot.',
  team: 'Net book value against current fair market value, by team.',
  site: 'Net book value against current fair market value, by site.',
};

function BookVsFmvPage() {
  const { classId } = Route.useSearch();
  // Epic 3 (lots-first-class) — one "Group by" control, one parameterized
  // endpoint (plan Deliverables §1). Point-in-time only — never wired into
  // a time-series chart (plan "Epic 3 — scope").
  const [dimension, setDimension] = useState<FinancialsGroupDimension>('class');
  const summaryQ = useQuery<BookVsFmvSummaryResponse>({
    queryKey: qk.financials.summary(dimension),
    queryFn: async () => {
      const res = await api.api.client.financials.summary.$get({ query: { dimension } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!('classes' in data)) throw new Error('unexpected summary response');
      return data as unknown as BookVsFmvSummaryResponse;
    },
  });

  return (
    <FinancialsReportScreen
      title="Financial Position"
      pinTo="/financials/book-vs-fmv"
      subtitle={DIMENSION_SUBTITLE[dimension]}
      query={summaryQ}
      // CSV/PDF export stays class-aggregated regardless of the on-screen
      // grouping (financials-export.ts is unchanged by Epic 3) — offering
      // it while grouped by lot/team/site would silently export a
      // different breakdown than what's on screen.
      exportCsv={
        dimension === 'class'
          ? { path: '/api/client/financials/export/summary.csv', filename: 'financials-book-vs-fmv-summary.csv' }
          : undefined
      }
      headerExtra={<GroupByControl value={dimension} onChange={setDimension} />}
    >
      {(data) => <BookVsFmvBody data={data} initialClassId={classId ?? null} />}
    </FinancialsReportScreen>
  );
}

// ---------------------------------------------------------------------------
// Body
//
// NOTE: this screen deliberately does NOT use FinancialsReportScreen's
// top-level `empty` — the policy banner and CoverageStatCard (SIL-271
// W4-T11, self-managed query) stay visible even when `classes` is empty, so
// the isEmpty branch is kept local to this body, same as depreciation's
// per-class "no policy" state.
// ---------------------------------------------------------------------------

function BookVsFmvBody({
  data,
  initialClassId,
}: {
  data: BookVsFmvSummaryResponse;
  /** SIL-566 — the class to reveal on mount, from ?classId= (Market
   * Outlook's deep link back to this class's row). Null on a plain visit. */
  initialClassId: string | null;
}) {
  const { classes, totals, dimension } = data;
  const isEmpty = classes.length === 0;
  const anyEstimate = classes.some((c) => c.isEstimate);
  // Lifted so the impairment watchlist can open the row it links to.
  // Keyed by `groupId` (works for every dimension) rather than the old
  // class-only `assetClassId` — for the 'class' dimension the two are the
  // same value, so the ?classId= deep link below is unaffected.
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(initialClassId);

  /** Opens a group's row and brings it into view — used by the watchlist
   * links (class dimension only) and the ?classId= deep link below. */
  const revealGroup = (groupId: string) => {
    setExpandedGroupId(groupId);
    // Next frame: the row exists already, but the panel below it changes
    // height, so scroll after React has committed the expansion.
    requestAnimationFrame(() => {
      document.getElementById(`class-${groupId}`)?.scrollIntoView({ block: 'center' });
    });
  };

  // SIL-566: a deep-linked classId still needs the scroll-into-view — the
  // useState initializer above already opened the row, but only `revealClass`
  // scrolls to it, and this component mounts already expanded rather than
  // going through a click.
  useEffect(() => {
    if (!initialClassId) return;
    requestAnimationFrame(() => {
      document.getElementById(`class-${initialClassId}`)?.scrollIntoView({ block: 'center' });
    });
    // Deliberately mount-only: re-running on every classes/expandedClassId
    // change would re-scroll the reader away from wherever they navigated to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-5">
      {data.usingOrgDefaultPolicy && <PolicyDefaultBanner />}

      {/* SIL-271 W4-T11: coverage stat on the Financials index (this screen
          is what bare /financials/ redirects to). */}
      <CoverageStatCard />

      {isEmpty ? (
        <EmptyState
          icon={Package}
          title="No financial data yet"
          description="Record purchase price and in-service dates on your assets to see book value against FMV here."
          action={<ImportFromErpLink />}
        />
      ) : (
        <>
          <OpportunityHero totals={totals} anyEstimate={anyEstimate} />

          {/* Stat tiles */}
          <MetricGrid>
            <StatTile
              label="Net Book Value"
              value={formatUsd(totals.netBookValueUsd)}
              estimate={anyEstimate}
            />
            <StatTile label="FMV (Med)" value={formatUsd(totals.fmvMedUsd)} />
            <GainLossTile
              value={totals.unrealizedGainLossUsd}
              gapWideningUsdPerQuarter={data.impairmentWatchlist?.gapWideningUsdPerQuarter ?? null}
            />
            <StatTile
              label="Recovery Floor"
              value={formatUsd(totals.itadBaselineUsd)}
              helper="if scrapped today (ITAD baseline)"
            />
          </MetricGrid>

          {/* FMV by <dimension> donut */}
          <Card>
            {/* No total here: the FMV (Med) stat tile directly above already
                states it. This card's job is the SHARE per group. */}
            <PanelTitle
              title={`FMV by ${DIMENSION_NOUN[dimension]}`}
              description="Share of current market value"
            />
            <div className="mt-3">
              <FmvByClassDonut classes={classes} />
            </div>
          </Card>

          {/* By-<dimension> table */}
          <Card>
            <PanelTitle
              title={`By ${DIMENSION_NOUN[dimension]}`}
              description={
                dimension === 'class'
                  ? '18-month FMV trend, unrealized gain/loss, and a suggested action per class'
                  : 'Book value, FMV, and unrealized gain/loss per group'
              }
            />
            <div className="mt-4 overflow-x-auto">
              <ByAssetClassTable
                classes={classes}
                dimension={dimension}
                expandedGroupId={expandedGroupId}
                onToggleGroup={setExpandedGroupId}
              />
            </div>
          </Card>

          {/* SIL-306 (W4, spec slice 5): impairment watchlist — classes whose
              FMV has run meaningfully below net book value, trailing streak,
              and a factual copy string the DTO ships pre-rendered. Epic 3
              (lots-first-class) — a genuinely per-CLASS concept (policy
              tier resolution), so the API only ever returns it for the
              'class' dimension; `data.impairmentWatchlist` is undefined for
              lot/team/site groups (never a fabricated cross-class watchlist). */}
          {data.impairmentWatchlist && (
            <ImpairmentWatchlistCard
              watchlist={data.impairmentWatchlist}
              onRevealClass={revealGroup}
            />
          )}

        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NBV variance drill-down — "ERP reports $X as of <date> — our model: $Y
// (+/-$delta)" per asset (spec §1 decision 5). Paginated, collapsed by
// default (spec §6 — unbounded per-asset arrays otherwise time out on large
// fleets). Only assets carrying a promoted ERP register row have a variance
// to show; everything else reads "no ERP data imported for this asset".
// ---------------------------------------------------------------------------

function NbvVariancePanel({
  assetClassId,
  assetClassName,
}: {
  assetClassId: string;
  assetClassName: string;
}) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [rows, setRows] = useState<BookVsFmvAssetRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const pageQ = useQuery({
    queryKey: qk.financials.summaryAssets(assetClassId, cursor),
    queryFn: async () => {
      const res = await api.api.client.financials.summary.$get({
        query: { assetClassId, limit: '25', ...(cursor ? { cursor } : {}) },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const page = await res.json();
      if (!('assets' in page)) throw new Error('unexpected summary drill-down response');
      setRows((prev) => (cursor === null ? page.assets : [...prev, ...page.assets]));
      setNextCursor(page.pageMeta.nextCursor);
      return page;
    },
  });

  const withErpData = useMemo(() => rows.filter((r) => r.erpReportedNbvUsd !== null), [rows]);

  const columns: ColumnDef<BookVsFmvAssetRow, unknown>[] = useMemo(
    () => [
      {
        id: 'assetId',
        header: 'Asset',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.serialNumber ?? row.original.assetId.slice(0, 8)}</span>
        ),
      },
      {
        id: 'ourNbv',
        header: () => <div className="text-right">Our model (NBV)</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <UsdCell value={row.original.netBookValueUsd} />
          </div>
        ),
      },
      {
        id: 'erpNbv',
        header: () => <div className="text-right">ERP reports</div>,
        cell: ({ row }) => (
          <div className="text-right">
            <UsdCell value={row.original.erpReportedNbvUsd} />
            {row.original.erpNbvAsOfDate && (
              <span className="ml-1 text-xs text-muted-foreground">
                as of {new Date(row.original.erpNbvAsOfDate).toLocaleDateString()}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'variance',
        header: () => <div className="text-right">Variance</div>,
        cell: ({ row }) => {
          const { netBookValueUsd, erpReportedNbvUsd } = row.original;
          if (netBookValueUsd === null || erpReportedNbvUsd === null) {
            return <div className="text-right text-muted-foreground">—</div>;
          }
          const delta = netBookValueUsd - erpReportedNbvUsd;
          return (
            <div className={`text-right tabular-nums ${delta === 0 ? 'text-muted-foreground' : delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {delta >= 0 ? '+' : ''}
              {formatUsd(delta)}
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="space-y-3 py-1">
      <SectionLabel>{assetClassName} — NBV variance vs ERP</SectionLabel>
      {!pageQ.isLoading && withErpData.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No {assetClassName} assets carry ERP-reported book value yet.{' '}
          <ImportFromErpLink className="font-medium text-brand-600 hover:underline" />
        </p>
      ) : (
        <DataTable
          data={withErpData}
          columns={columns}
          emptyMessage={pageQ.isLoading ? 'Loading…' : 'No ERP-reported NBV yet.'}
        />
      )}
      {nextCursor && (
        <button
          type="button"
          onClick={() => setCursor(nextCursor)}
          disabled={pageQ.isFetching}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
        >
          {pageQ.isFetching ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dismissible "using org default policy" banner
// ---------------------------------------------------------------------------

function PolicyDefaultBanner() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(POLICY_BANNER_DISMISS_KEY) === '1',
  );

  if (dismissed) return null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3">
      <Info className="mt-0.5 size-5 shrink-0 text-brand-600" />
      <p className="flex-1 text-sm text-brand-700">
        This report is using your organization's default depreciation policy. Set a
        per-class override on the Capitalization Policy screen for asset classes that
        depreciate differently.
      </p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => {
          localStorage.setItem(POLICY_BANNER_DISMISS_KEY, '1');
          setDismissed(true);
        }}
        className="shrink-0 rounded-md p-1 text-brand-600 hover:bg-brand-100"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Opportunity-first hero — "cost of not deciding" + ITAD-vs-hold headline
// ---------------------------------------------------------------------------

function OpportunityHero({
  totals,
  anyEstimate,
}: {
  totals: BookVsFmvSummaryResponse['totals'];
  anyEstimate: boolean;
}) {
  const { opportunityCostUsd, fmvMedUsd, itadBaselineUsd } = totals;
  const holding = opportunityCostUsd !== null && opportunityCostUsd > 0;

  return (
    <div className="rounded-xl border border-border bg-gradient-to-br from-zinc-900 to-zinc-800 p-6 text-white shadow-sm sm:p-8">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        <Scale className="size-3.5" />
        The cost of not deciding
        {anyEstimate && <EstimateBadge dark />}
      </div>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-3xl font-semibold tabular-nums sm:text-4xl">
          {opportunityCostUsd === null ? '—' : formatUsd(Math.abs(opportunityCostUsd), { compact: true })}
        </span>
        <span className="text-sm text-muted-foreground">
          {opportunityCostUsd === null
            ? 'Not enough data to estimate opportunity cost'
            : holding
              ? 'more value available by acting now than by continuing to hold'
              : 'Baseline already exceeds current FMV — no opportunity gap'}
        </span>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 border-t border-white/10 pt-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Hold (current FMV, med)
          </p>
          <p className="mt-1 text-xl font-semibold tabular-nums">{formatUsd(fmvMedUsd)}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            ITAD now (baseline)
          </p>
          <p className="mt-1 text-xl font-semibold tabular-nums">{formatUsd(itadBaselineUsd)}</p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat tiles
// ---------------------------------------------------------------------------

function StatTile({
  label,
  value,
  estimate,
  helper,
}: {
  label: string;
  value: string;
  estimate?: boolean;
  /** Small subtitle under the value, e.g. "if scrapped today (ITAD baseline)". */
  helper?: string;
}) {
  return (
    <Card padding="md">
      <div className="flex items-center gap-1.5">
        <SectionLabel>
          {label}
        </SectionLabel>
        {estimate && <EstimateBadge />}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
      {helper && <p className="mt-1 text-xs text-muted-foreground">{helper}</p>}
    </Card>
  );
}

function GainLossTile({
  value,
  gapWideningUsdPerQuarter,
}: {
  value: number | null;
  /** SIL-306 (W4, spec slice 5) — impairment watchlist's portfolio-wide gap
   * slope, USD/quarter. Negative = the FMV-below-book shortfall is
   * widening. Null when there isn't enough trailing data for a slope. */
  gapWideningUsdPerQuarter?: number | null;
}) {
  const positive = value !== null && value >= 0;
  return (
    <Card padding="md">
      <SectionLabel>
        Unrealized Gain/Loss
      </SectionLabel>
      <p
        className={`mt-2 text-2xl font-semibold tabular-nums ${
          value === null ? 'text-foreground' : positive ? 'text-emerald-600' : 'text-red-500'
        }`}
      >
        {value === null ? '—' : `${positive ? '+' : ''}${formatUsd(value)}`}
      </p>
      {gapWideningUsdPerQuarter != null && (
        <p className="mt-1 text-xs text-muted-foreground">
          gap {gapWideningUsdPerQuarter < 0 ? 'widening' : 'narrowing'} ~
          {formatUsd(Math.abs(gapWideningUsdPerQuarter), { compact: true })}/quarter
        </p>
      )}
    </Card>
  );
}

/** Small "estimate" chip — per-figure flag driven by `isEstimate` on the DTO
 * (book value falling back to SKU launch price rather than purchase price). */
function EstimateBadge({ dark }: { dark?: boolean }) {
  return (
    <span
      title="Estimated — falls back to SKU launch price where purchase price is unknown"
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${
        dark ? 'bg-card/10 text-muted-foreground' : 'bg-muted text-muted-foreground'
      }`}
    >
      est.
    </span>
  );
}

// ---------------------------------------------------------------------------
// FMV by class donut
// ---------------------------------------------------------------------------

const DONUT_COLORS = ['#8B5CF6', '#2FD08C', '#F5B44C', '#4C9AF0', '#EF4444', '#0EA5E9'];

function FmvByClassDonut({ classes }: { classes: BookVsFmvClassRow[] }) {
  const total = classes.reduce((s, c) => s + (c.fmvMedUsd ?? 0), 0);
  if (total <= 0) {
    return (
      <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
        No FMV yet.
      </div>
    );
  }
  const sorted = [...classes].sort((a, b) => (b.fmvMedUsd ?? 0) - (a.fmvMedUsd ?? 0));
  const R = 54;
  const C = 2 * Math.PI * R;
  let offset = 0;
  const segments = sorted.map((c, i) => {
    const val = c.fmvMedUsd ?? 0;
    const frac = val / total;
    const seg = { ...c, frac, len: frac * C, offset, color: DONUT_COLORS[i % DONUT_COLORS.length] };
    offset += frac * C;
    return seg;
  });
  return (
    <div>
      <div className="flex justify-center">
        <svg viewBox="0 0 160 160" className="w-[150px]">
          {segments.map((s) => (
            <circle
              key={s.groupId ?? 'unassigned'}
              cx="80"
              cy="80"
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth="26"
              strokeDasharray={`${s.len} ${C - s.len}`}
              strokeDashoffset={-s.offset}
              transform="rotate(-90 80 80)"
            />
          ))}
        </svg>
      </div>
      <div className="mt-3 flex flex-col gap-2">
        {segments.map((s) => (
          <div key={s.groupId ?? 'unassigned'} className="flex items-center gap-2.5 text-xs">
            <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
            <span className="text-muted-foreground">{s.groupLabel}</span>
            <span className="ml-auto text-sm font-semibold tabular-nums text-foreground">
              {formatUsd(s.fmvMedUsd, { compact: true })}
            </span>
            <span className="w-8 text-right tabular-nums text-muted-foreground">
              {Math.round(s.frac * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// By-asset-class table — 18mo FMV sparkline, delta, and a suggested action.
//
// The suggested action is still derived in the browser (there is no
// `suggestedAction` field on the DTO), but it is no longer silent about it:
// the kernel, its bands, and its `formulaId` live in
// `@siliconx/shared`'s `suggested-action.ts`, and the pill now carries the
// same `CalculationBasisTrigger` + "est." chip every other figure on this
// screen carries. The basis discloses that the band is measured from two
// sampled endpoints rather than a fitted trend — the part a board member asks
// about. Replacing the method with a server-side trailing slope is tracked
// separately; this makes it auditable, not correct.
// ---------------------------------------------------------------------------

/** The pill's basis, assembled through the same `basisOf()` seam the API
 * uses, so the drawer renders it identically to a server-sent basis. */
function suggestedActionBasis(row: BookVsFmvClassRow): CalculationBasis {
  return basisOf({
    formulaId: SUGGESTED_ACTION_FORMULA_ID,
    inputs: describeSuggestedActionBasis(row.trend),
    constants: ['suggestedActionSellSoonPct', 'suggestedActionMonitorPct', 'suggestedActionRisingPct'],
  });
}

/**
 * The "est." marker is folded INSIDE the pill rather than set beside it: the
 * pill is the estimate, and a sibling chip in this cell would read as
 * qualifying the row instead of the recommendation. Weight carries the
 * demotion (`font-normal` against the label's `font-medium`) so the marker
 * keeps the pill's own text color and stays at full contrast — a lighter tint
 * would fail on these tinted beds.
 */
function ActionPill({ action }: { action: SuggestedAction }) {
  const styles: Record<SuggestedAction['tone'], string> = {
    positive: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    negative: 'bg-red-50 text-red-700 ring-red-200',
    muted: 'bg-muted text-muted-foreground ring-border',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${styles[action.tone]}`}
    >
      {action.label}
      <span aria-hidden="true" className="font-normal opacity-60">
        ·
      </span>
      <span className="font-normal">est.</span>
    </span>
  );
}

function TrendDelta({ row }: { row: BookVsFmvClassRow }) {
  const first = row.trend[0];
  const last = row.trend[row.trend.length - 1];
  if (!first || !last || first.fmvMedUsd === 0) {
    return <Minus className="size-3.5 text-muted-foreground" />;
  }
  const pct = ((last.fmvMedUsd - first.fmvMedUsd) / first.fmvMedUsd) * 100;
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium tabular-nums ${
        positive ? 'text-emerald-600' : 'text-red-500'
      }`}
    >
      <Icon className="size-3.5" />
      {positive ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  );
}

function ClassSparkline({ row }: { row: BookVsFmvClassRow }) {
  if (row.trend.length < 2) {
    return <div className="h-9 w-24 text-center text-xs leading-9 text-muted-foreground">n/a</div>;
  }
  const last = row.trend[row.trend.length - 1];
  const first = row.trend[0];
  const positive = last && first ? last.fmvMedUsd >= first.fmvMedUsd : true;
  const color = positive ? '#2FD08C' : '#EF4444';
  return (
    <div className="h-9 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={row.trend} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`spark-${row.assetClassId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <RechartsTooltip
            contentStyle={{ fontSize: 12, borderRadius: 6, padding: '4px 8px' }}
            labelFormatter={(l) => String(l)}
            formatter={(v: number) => [formatUsd(v, { compact: true }), 'FMV (med)']}
          />
          <Area
            type="monotone"
            dataKey="fmvMedUsd"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#spark-${row.assetClassId})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * The per-class ERP drill-down lives INSIDE this table as a row expansion.
 * It used to be one collapsed `<Card>` per asset class stacked below the page,
 * so an org with nine classes got nine near-identical "Show X NBV variance vs
 * ERP" buttons and the impairment watchlist's class link landed on a collapsed
 * button instead of the detail it promised.
 *
 * `expandedClassId` is lifted to the page so the watchlist can open a row.
 */
function ByAssetClassTable({
  classes,
  dimension,
  expandedGroupId,
  onToggleGroup,
}: {
  classes: BookVsFmvClassRow[];
  dimension: FinancialsGroupDimension;
  expandedGroupId: string | null;
  onToggleGroup: (groupId: string | null) => void;
}) {
  const isClass = dimension === 'class';
  return (
    <table className="w-full min-w-[900px] text-sm">
      <thead>
        <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <th className="w-8 py-2" />
          <Th>{DIMENSION_NOUN_CAPITALIZED[dimension]}</Th>
          <Th align="right">Assets</Th>
          <Th align="right">Net Book Value</Th>
          <Th align="right">FMV (Med)</Th>
          <Th align="right">Unrealized G/L</Th>
          {isClass && <Th>18mo Trend</Th>}
          {isClass && <Th>Suggested Action</Th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {classes.map((row) => {
          const gain = row.unrealizedGainLossUsd;
          const action = suggestedActionFromTrend(row.trend);
          const rowId = row.groupId ?? 'unassigned';
          const open = expandedGroupId === row.groupId;
          return (
            <Fragment key={rowId}>
            <tr id={`class-${rowId}`} className="scroll-mt-24">
              <td className="py-3 pr-1 align-middle">
                {isClass ? (
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={`class-panel-${rowId}`}
                    aria-label={`${open ? 'Hide' : 'Show'} ${row.groupLabel} NBV variance vs ERP`}
                    onClick={() => onToggleGroup(open ? null : row.groupId)}
                    className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                  </button>
                ) : null}
              </td>
              <Td>
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                  {row.groupLabel}
                  {row.basis && (
                    <CalculationBasisTrigger title={`${row.groupLabel} — Book value`} basis={row.basis} />
                  )}
                </span>
                {/* SIL-566 (I2-B4): cross-pillar deep link to Market Outlook —
                    only meaningful for the 'class' dimension (Market Outlook
                    is itself class-scoped; a lot/team/site spans classes). */}
                {isClass && row.assetClassId && (
                  <Link
                    to="/market-trends"
                    search={{ classId: row.assetClassId }}
                    title={`View ${row.groupLabel} in Market Outlook`}
                    className="ml-1.5 inline-flex items-center gap-0.5 text-xs font-medium text-brand-600 hover:underline"
                  >
                    Market outlook <ArrowUpRight className="size-3" />
                  </Link>
                )}
              </Td>
              <Td align="right">
                <span className="tabular-nums text-foreground">
                  {row.assetCount.toLocaleString()}
                </span>
              </Td>
              <Td align="right">
                <span className="inline-flex items-center gap-1 tabular-nums text-foreground">
                  {formatUsd(row.netBookValueUsd)}
                  {row.isEstimate && <EstimateBadge />}
                </span>
              </Td>
              <Td align="right">
                <span className="tabular-nums text-foreground">{formatUsd(row.fmvMedUsd)}</span>
              </Td>
              <Td align="right">
                <span
                  className={`tabular-nums ${
                    gain === null ? 'text-muted-foreground' : gain >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}
                >
                  {gain === null ? '—' : `${gain >= 0 ? '+' : ''}${formatUsd(gain)}`}
                </span>
              </Td>
              {isClass && (
                <Td>
                  <div className="flex items-center gap-2">
                    <ClassSparkline row={row} />
                    <TrendDelta row={row} />
                  </div>
                </Td>
              )}
              {isClass && (
                <Td>
                  <span className="inline-flex items-center gap-1">
                    <ActionPill action={action} />
                    <CalculationBasisTrigger
                      title={`${row.groupLabel} — Suggested action`}
                      basis={suggestedActionBasis(row)}
                    />
                  </span>
                </Td>
              )}
            </tr>
            {open && isClass && row.assetClassId && (
              <tr id={`class-panel-${rowId}`}>
                <td colSpan={8} className="bg-muted px-4 pb-4 pt-1">
                  <NbvVariancePanel
                    assetClassId={row.assetClassId}
                    assetClassName={row.groupLabel}
                  />
                </td>
              </tr>
            )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' }) {
  return <th className={`py-2 pr-4 ${align === 'right' ? 'text-right' : ''}`}>{children}</th>;
}

function Td({ children, align }: { children: ReactNode; align?: 'right' }) {
  return <td className={`py-3 pr-4 align-middle ${align === 'right' ? 'text-right' : ''}`}>{children}</td>;
}

// ---------------------------------------------------------------------------
// Impairment watchlist (SIL-306, W4, spec slice 5) — classes whose FMV has
// run meaningfully below net book value. `copy`/`evidenceCopy` are rendered
// VERBATIM (the DTO ships them pre-rendered — spec: "shipped copy stays
// factual"). ASC 360 appears ONLY inside the header's info tooltip, worded
// "commonly associated with ASC 360 review" — never as a headline, status,
// or assertion that an impairment is required; this is accounting-judgment
// territory the product must not appear to determine on its own.
// ---------------------------------------------------------------------------

const TIER_CHIP_STYLES: Record<Exclude<ImpairmentTier, 'none'>, string> = {
  watch: 'bg-amber-50 text-amber-700 ring-amber-200',
  review: 'bg-orange-50 text-orange-700 ring-orange-200',
  likely: 'bg-red-50 text-red-700 ring-red-200',
};

// Thresholds mirrored from classifyImpairmentTier() in
// packages/shared/src/impairment-watchlist.ts — keep in sync if those change.
const TIER_TITLES: Record<Exclude<ImpairmentTier, 'none'>, string> = {
  watch: 'FMV at least 10% below net book value',
  review: 'FMV at least 20% below net book value, or below for 2+ consecutive quarters',
  likely: 'FMV at least 30% below net book value for 2+ consecutive quarters',
};

function TierChip({ tier }: { tier: ImpairmentTier }) {
  if (tier === 'none') return null;
  return (
    <span
      title={TIER_TITLES[tier]}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${TIER_CHIP_STYLES[tier]}`}
    >
      {tier}
    </span>
  );
}

function ImpairmentWatchlistCard({
  watchlist,
  onRevealClass,
}: {
  watchlist: ImpairmentWatchlist;
  /** Opens the class's row in the By-asset-class table and scrolls to it. A bare
   * `#class-…` anchor used to land on a COLLAPSED card, so the link promised
   * detail and delivered a button. */
  onRevealClass: (assetClassId: string) => void;
}) {
  if (watchlist.classes.length === 0) return null;

  return (
    <Card>
      <PanelTitle
        title="Impairment watchlist"
        right={
          <InfoTooltip label="About impairment tiers">
            Tiers reflect how far, and for how long, a class's FMV has run below net book
            value — commonly associated with ASC 360 review. This is not an accounting
            determination; consult your accounting policy for an actual impairment
            assessment.
          </InfoTooltip>
        }
        description={
          watchlist.gapWideningUsdPerQuarter != null
            ? `Portfolio gap ${watchlist.gapWideningUsdPerQuarter < 0 ? 'widening' : 'narrowing'} ~${formatUsd(Math.abs(watchlist.gapWideningUsdPerQuarter), { compact: true })}/quarter`
            : undefined
        }
      />
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Th>Class</Th>
              <Th align="right">Gap $</Th>
              <Th align="right">Gap %</Th>
              <Th align="right">Qtrs below</Th>
              <Th>Rating</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {watchlist.classes.map((row) => (
              <tr key={row.assetClassId}>
                <Td>
                  <button
                    type="button"
                    onClick={() => onRevealClass(row.assetClassId)}
                    className="inline-flex items-center gap-1.5 font-medium text-brand-600 hover:underline"
                  >
                    {row.assetClassName}
                  </button>
                  <CalculationBasisTrigger
                    title={`${row.assetClassName} — Impairment watchlist`}
                    basis={row.basis}
                  />
                  <p className="mt-0.5 max-w-sm text-xs text-muted-foreground">{row.copy}</p>
                  {row.evidenceCopy && (
                    <p className="mt-0.5 max-w-sm text-xs text-muted-foreground">{row.evidenceCopy}</p>
                  )}
                </Td>
                <Td align="right">
                  <span className="tabular-nums text-red-500">
                    {row.gapUsd === null ? '—' : formatUsd(row.gapUsd)}
                  </span>
                </Td>
                <Td align="right">
                  <span className="tabular-nums text-red-500">
                    {row.gapPct === null ? '—' : `${row.gapPct.toFixed(1)}%`}
                  </span>
                </Td>
                <Td align="right">
                  <span className="tabular-nums text-foreground">{row.consecutiveQuartersBelow}</span>
                </Td>
                <Td>
                  <TierChip tier={row.tier} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
